import {Component, EventEmitter, Input, Output} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {AmountPipe, ToastService, TranslationService} from '@emma-labs/shared/angular';
import {
  Dropdown,
  Duration,
  durationToNumber,
  findClosestValue,
  PersonInsuranceType,
  RiderType,
} from '@emma-labs/shared/typescript';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {DividerModule} from 'primeng/divider';
import {DropdownModule} from 'primeng/dropdown';
import {ProgressSpinnerModule} from 'primeng/progressspinner';
import {TooltipModule} from 'primeng/tooltip';
import {distinctUntilChanged} from 'rxjs/operators';

interface ProductSelectionForm {
  coverageAmount: FormControl<number | null>;
  duration: FormControl<Duration | number | null>;
}

@UntilDestroy()
@Component({
  selector: 'em-product-selector-card',

  imports: [
    DropdownModule,
    ReactiveFormsModule,
    DividerModule,
    ProgressSpinnerModule,
    TooltipModule,
    AmountPipe,
  ],
  templateUrl: './product-selector-card.component.html',
})
export class ProductSelectorCardComponent {
  @Input({required: true}) title!: string;

  @Input({required: true}) productType!: PersonInsuranceType | RiderType;

  @Input({required: true}) set coverageAmountOptions(options: Dropdown<number>[]) {
    this._coverageAmountOptions = options;

    // If the current value is not in the options, find the closest one and set it
    const currentValue = this.form.controls.coverageAmount.value;
    if (currentValue && !options.some((o) => o.value === currentValue)) {
      const closestOption = findClosestValue(
        currentValue,
        options.map((o) => o.value),
      );

      if (closestOption) {
        this.form.controls.coverageAmount.setValue(closestOption);

        if (this.withToasterAlerts) {
          this.displayAutomaticModificationAlert();
        }
      } else {
        this.form.controls.coverageAmount.setValue(null);
      }
    }
  }

  @Input({required: true}) withToasterAlerts = false;

  @Input() set durationOptions(options: Dropdown<Duration | number>[]) {
    this._durationOptions = options;

    // If the current value is not in the options, find the closest one and set it.
    const currentValue = this.form.controls.duration.value;
    if (currentValue && !options.some((o) => o.value === currentValue)) {
      const numericCurrentValue =
        this.productType === 'TERM' ? durationToNumber(currentValue as Duration) : (currentValue as number);

      const closestOption = findClosestValue(
        numericCurrentValue,
        options.map((o) => (typeof o.value === 'string' ? durationToNumber(o.value) : o.value)),
      );

      if (closestOption) {
        this.form.controls.duration.setValue(
          this.productType === 'TERM'
            ? (`T${closestOption}` as Duration)
            : this.productType === 'WHOLE'
              ? (`P${closestOption}` as Duration) // This case only occurs when P100 is the only remaining option, so we can assume a prefix of P
              : closestOption,
        );

        if (this.withToasterAlerts) {
          this.displayAutomaticModificationAlert();
        }
      } else {
        this.form.controls.duration.setValue(null);
      }
    }

    if (!this.form.controls.duration.hasValidator(Validators.required)) {
      this.form.controls.duration.addValidators([Validators.required]);
    }
  }

  @Input({required: true}) set price(price: number | undefined) {
    this._price = price;
    this.isPriceLoading = false;
  }

  @Input({required: true}) set prefilledCoverage(
    coverage: {coverageAmount: number; duration?: Duration | number} | undefined,
  ) {
    if (coverage) {
      this.form.controls.coverageAmount.setValue(coverage.coverageAmount, {emitEvent: false});

      if (coverage.duration) {
        this.form.controls.duration.setValue(coverage.duration, {emitEvent: false});
        this.form.controls.duration.enable({emitEvent: false});
      }
    }
  }

  @Input() titleTooltip?: string;

  @Input() durationTooltip?: string;

  @Output() inputsAltered = new EventEmitter<{
    coverageAmount: number;
    duration?: Duration | number;
  }>();

  @Output() renewalPricesPopupRequested = new EventEmitter<void>();

  form: FormGroup<ProductSelectionForm>;
  _durationOptions?: Dropdown<Duration | number>[];
  _coverageAmountOptions?: Dropdown<number>[];
  _price?: number;
  isPriceLoading = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly translate: TranslationService,
    private readonly toastService: ToastService,
  ) {
    this.form = this.createForm();
  }

  resetProduct() {
    this.form.reset({});
    this.form.controls.duration.disable({emitEvent: false});
    this._price = undefined;
  }

  openRenewalPricesDialog() {
    this.renewalPricesPopupRequested.emit();
  }

  private displayAutomaticModificationAlert(): void {
    this.toastService.warn(
      this.translate.currentLanguageIsFr()
        ? `La protection a été modifiée.`
        : `The protection has been modified.`,
    );
  }

  private createForm(): FormGroup<ProductSelectionForm> {
    const form = this.fb.group({
      coverageAmount: this.fb.control<number | null>(null, [Validators.required]),
      duration: this.fb.control<Duration | number | null>({value: null, disabled: true}),
    });

    form.controls.coverageAmount.valueChanges
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((value) => {
        if (value) {
          form.controls.duration.enable({emitEvent: false});
        } else {
          form.controls.duration.disable({emitEvent: false});
        }
      });

    form.valueChanges.pipe(distinctUntilChanged(), untilDestroyed(this)).subscribe((value) => {
      this.isPriceLoading = form.valid;
      this.inputsAltered.emit({
        coverageAmount: value.coverageAmount!,
        duration: value.duration ?? undefined,
      });
    });

    return form;
  }
}
